import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Scale, ChevronRight, ArrowLeft } from 'lucide-react';
import { nurDatum } from '../lib/zeit';

// Expliziter B2B-Audience-Vertrag. Neue öffentliche Dokumenttypen werden nicht
// automatisch in das Partnerportal übernommen.
export const PARTNER_LEGAL_TYPES = Object.freeze([
  'agb_partner',
  'provision_abrechnung',
  'community_sicherheit',
  'verhalten_antidiskriminierung',
  'null_toleranz',
  'beschwerde_ueberpruefung',
  'datenschutz_partnerportal',
  'impressum',
  'kontoloeschung',
]);
const PARTNER_LEGAL_TYPE_SET = new Set(PARTNER_LEGAL_TYPES);
const isPartnerLegalDocument = (item) => {
  const type = item?.doc_type || item?.type;
  return PARTNER_LEGAL_TYPE_SET.has(type)
    && item?.visibility === 'public'
    && Array.isArray(item?.audience)
    && item.audience.includes('partner');
};

export default function Legal() {
  const [docs, setDocs] = useState([]);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get('/legal')
      .then((response) => {
        const listed = (response.data.docs || []).filter(isPartnerLegalDocument);
        const order = new Map(PARTNER_LEGAL_TYPES.map((type, index) => [type, index]));
        listed.sort((a, b) => order.get(a.doc_type || a.type) - order.get(b.doc_type || b.type));
        setDocs(listed);
        setError('');
      })
      .catch(() => setError('Die Partner-Rechtstexte konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  const open = (type) => {
    if (!PARTNER_LEGAL_TYPE_SET.has(type)) return;
    setLoading(true);
    api.get(`/legal/${type}`)
      .then((response) => {
        const opened = response.data.doc;
        if (!isPartnerLegalDocument(opened) || (opened.doc_type || opened.type) !== type) {
          throw new Error('invalid_partner_legal_document');
        }
        setDoc(opened);
        setError('');
      })
      .catch(() => setError('Der Rechtstext konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  };

  if (doc) {
    return (
      <div style={{ maxWidth: 820 }}>
        <button onClick={() => setDoc(null)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f3f4f6', borderRadius: 20, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', marginBottom: 18 }}>
          <ArrowLeft size={14} /> Alle Rechtstexte
        </button>
        <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -0.5, marginBottom: 4 }}>{doc.title}</h1>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>Version {doc.version} · Stand {doc.published_at ? nurDatum(doc.published_at) : ''}</div>
        <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: '#1f2937' }}>{doc.content}</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -1, marginBottom: 6 }}>Rechtliches</h1>
      <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 22, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Scale size={14} /> Rechtstexte für VOOGO-Partnerunternehmen in der aktuell gültigen Version
      </div>
      {error && <div role="alert" style={{ padding: '12px 14px', marginBottom: 14, border: '1px solid #fca5a5', borderRadius: 10, background: '#fef2f2', color: '#991b1b', fontSize: 13 }}>{error}</div>}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', opacity: loading ? 0.6 : 1 }}>
        {docs.map((d, i) => (
          <button key={d.doc_type || d.type} onClick={() => open(d.doc_type || d.type)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '15px 18px', background: '#fff', border: 'none', borderTop: i ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', textAlign: 'left' }}>
            <span>
              <span style={{ fontSize: 14, fontWeight: 700, display: 'block' }}>{d.title}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>Version {d.version}</span>
            </span>
            <ChevronRight size={16} color="#6b7280" />
          </button>
        ))}
        {!docs.length && <div style={{ padding: 30, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>{loading ? 'Wird geladen …' : 'Keine Partner-Rechtstexte verfügbar.'}</div>}
      </div>
    </div>
  );
}
